import { useCallback, useEffect, useMemo, useState } from "react";
import "./TransferContact.css";

/* ═══════════════════════════════════════════════════════
   API CONFIG
   ═══════════════════════════════════════════════════════ */
const RAW_API_BASE =
  import.meta.env?.VITE_API_URL ||
  import.meta.env?.VITE_API_BASE_URL ||
  "https://www.thehexaplay.com";

const normalizeApiBase = (base) => {
  const clean = String(base || "")
    .trim()
    .replace(/\/+$/, "");

  if (!clean) return "/api";
  if (clean.endsWith("/api")) return clean;

  return `${clean}/api`;
};

const API_BASE = normalizeApiBase(RAW_API_BASE);

const ORDERS_API = `${API_BASE}/orders`;
const TRANSFER_API = `${API_BASE}/transfer/contact-to-staff`;
const TRANSFER_LIST_API = `${API_BASE}/transfer/list`;
const TRANSFER_DELETE_API = `${API_BASE}/transfer`;
const ORDER_DELETE_API = `${API_BASE}/transfer/order`;

/* native-driver pending endpoint — Mongoose can never strip the
   handover / extra-person / custom fields through this route */
const PENDING_ORDERS_API = `${API_BASE}/transfer/pending`;

const getOrderUpdateApi = (id) => `${ORDER_DELETE_API}/${id}`;
const getTransferUpdateApi = (id) => `${TRANSFER_DELETE_API}/${id}`;

/*
  Endpoints used by the edit modal:
    PATCH /api/transfer/order/:id   ← pending orders
    PATCH /api/transfer/:id         ← transferred bookings
  Both are handled by the same transfer router and retry with PUT
  when PATCH is unavailable.
*/

/* ═══════════════════════════════════════════════════════
   NUMERIC / STRING HELPERS
   ═══════════════════════════════════════════════════════ */
const n = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toCount = (value) => {
  const parsed = Math.floor(Number(value));
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return parsed;
};

const toAmount = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.round(parsed * 100) / 100;
};

const money = (value) => `₹${n(value).toLocaleString("en-IN")}`;

const trimValue = (value) => String(value ?? "").trim();

const safeQty = (item) =>
  Math.max(
    1,
    Math.floor(n(item?.qty || item?.quantity || item?.count || 1))
  );

const qtyNosLabel = (item) => {
  const quantity = safeQty(item);
  return quantity > 1 ? ` x ${quantity} Nos` : "";
};

const qtyChipLabel = (item) => {
  const quantity = safeQty(item);
  return quantity > 1 ? `×${quantity}` : "";
};

const addonDisplayPrice = (item) => n(item?.unitPrice || item?.price);

const addonLineTotal = (item) => addonDisplayPrice(item) * safeQty(item);

const formatTS = (timestamp) => {
  if (!timestamp) return "—";

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const parseDateToYYYYMMDD = (dateString) => {
  if (!dateString) return null;

  const value = String(dateString).trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

  const match = value.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);

  if (match) {
    return `${match[3]}-${match[2].padStart(2, "0")}-${match[1].padStart(
      2,
      "0"
    )}`;
  }

  const parsedDate = new Date(value);

  if (!Number.isNaN(parsedDate.getTime())) {
    return parsedDate.toISOString().slice(0, 10);
  }

  return null;
};

const fetchWithTimeout = async (url, options = {}, timeout = 12000) => {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeout);

  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    window.clearTimeout(timer);
  }
};

const readResponseBody = async (response) => {
  const text = await response.text();
  if (!text) return {};

  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
};

const requestUpdate = async (url, payload) => {
  const methods = ["PATCH", "PUT"];
  let lastError = null;

  for (let index = 0; index < methods.length; index += 1) {
    const method = methods[index];

    try {
      const response = await fetchWithTimeout(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await readResponseBody(response);

      if (response.ok) return result;

      const canRetryWithPut =
        index === 0 && [404, 405, 501].includes(response.status);

      if (canRetryWithPut) {
        lastError = new Error(
          result.message || `${method} update route is unavailable`
        );
        continue;
      }

      throw new Error(
        result.message ||
          result.error ||
          `Unable to update record (${response.status})`
      );
    } catch (error) {
      lastError = error;

      if (error.name === "AbortError") {
        throw new Error("The update request timed out. Please try again.");
      }

      if (index === methods.length - 1) throw error;
    }
  }

  throw lastError || new Error("Unable to update record");
};

/* ═══════════════════════════════════════════════════════
   ADD-ONS
   ═══════════════════════════════════════════════════════ */
const buildAddonRows = (order) => {
  const cakes = Array.isArray(order?.cakes) ? order.cakes : [];
  const extraDecors = Array.isArray(order?.extraDecors)
    ? order.extraDecors
    : [];
  const gifts = Array.isArray(order?.gifts) ? order.gifts : [];
  const services = Array.isArray(order?.services) ? order.services : [];

  return [
    ...cakes.map((item) => ({
      ...item,
      emoji: "🎂",
      category: "Cake",
      categoryLabel: "Cakes",
    })),
    ...extraDecors.map((item) => ({
      ...item,
      emoji: "🎨",
      category: "Decor",
      categoryLabel: "Decorations",
    })),
    ...gifts.map((item) => ({
      ...item,
      emoji: "🎁",
      category: "Gift",
      categoryLabel: "Gifts",
    })),
    ...services.map((item) => ({
      ...item,
      emoji: "🛎️",
      category: "Service",
      categoryLabel: "Services",
    })),
  ];
};

const formatAddonDetails = (order) => {
  const addons = buildAddonRows(order);
  if (addons.length === 0) return null;

  return {
    gifts: addons.filter((a) => a.category === "Gift"),
    cakes: addons.filter((a) => a.category === "Cake"),
    decors: addons.filter((a) => a.category === "Decor"),
    services: addons.filter((a) => a.category === "Service"),
    all: addons,
  };
};

const addonsAmountOf = (record) => {
  const rows = buildAddonRows(record);

  if (rows.length > 0) {
    return rows.reduce((sum, row) => sum + addonLineTotal(row), 0);
  }

  return n(record?.addonsAmount || record?.addonsTotal || record?.otherCharges);
};

/* ═══════════════════════════════════════════════════════
   EXTRA PERSON PRICING
   ───────────────────────────────────────────────────────
   extraPersons       = totalPersons − packageIncludedPersons
   chargeablePersons  = extraPersons − complimentaryPersons
   finalPerPerson     = standardCostPerPerson − discountPerPerson
   extraPersonAmount  = chargeablePersons × finalPerPerson
   ★ complimentary persons are NEVER charged.
   ═══════════════════════════════════════════════════════ */
const computeExtraPersonPricing = (values = {}) => {
  const totalPersons = toCount(values.totalPersons);
  const packageIncludedPersons = toCount(values.packageIncludedPersons);

  const extraPersons = Math.max(0, totalPersons - packageIncludedPersons);

  const complimentaryPersons = Math.min(
    toCount(values.complimentaryPersons),
    extraPersons
  );

  const chargeablePersons = Math.max(0, extraPersons - complimentaryPersons);

  const standardCostPerPerson = toAmount(values.standardCostPerPerson);

  const discountPerPerson = Math.min(
    toAmount(values.discountPerPerson),
    standardCostPerPerson
  );

  const finalCostPerPerson = Math.max(
    0,
    standardCostPerPerson - discountPerPerson
  );

  return {
    totalPersons,
    packageIncludedPersons,
    extraPersons,
    complimentaryPersons,
    chargeablePersons,
    standardCostPerPerson,
    discountPerPerson,
    finalCostPerPerson,
    extraPersonAmount: chargeablePersons * finalCostPerPerson,
    isActive: extraPersons > 0 || finalCostPerPerson > 0,
  };
};

const firstFiniteNumber = (record, keys, fallback = 0) => {
  if (!record) return fallback;

  const containers = [
    record,
    record.bookingDetails,
    record.handoverDetails,
    record.orderSnapshot,
    record.details,
  ].filter(Boolean);

  for (const container of containers) {
    for (const key of keys) {
      const value = container[key];
      if (value !== undefined && value !== null && value !== "") {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) return parsed;
      }
    }
  }

  return fallback;
};

const packageAmountOf = (record) => {
  const explicit = firstFiniteNumber(record, [
    "packageAmount",
    "packagePrice",
    "baseAmount",
    "packageBaseAmount",
  ]);

  if (explicit > 0) return explicit;

  const rawTotal = firstFiniteNumber(record, ["total", "amount"]);
  const addons = addonsAmountOf(record);
  const storedExtra = n(record?.extraPersonDetails?.extraPersonAmount);
  const storedDiscounts =
    firstFiniteNumber(record, ["managerDiscount"]) +
    firstFiniteNumber(record, ["otherDiscount", "extraDiscount"]);

  return Math.max(0, rawTotal - addons - storedExtra + storedDiscounts);
};

/* ═══════════════════════════════════════════════════════
   DEMO FALLBACK (used only when the API is unreachable so the
   screen stays fully explorable / previewable)
   ═══════════════════════════════════════════════════════ */
const DEMO_EXTRA_DETAILS = {
  totalPersons: 10,
  packageIncludedPersons: 4,
  complimentaryPersons: 0,
  extraPersons: 6,
  chargeablePersons: 6,
  standardCostPerPerson: 300,
  discountPerPerson: 50,
  finalCostPerPerson: 250,
  extraPersonAmount: 1500,
  isActive: true,
};

const DEMO_PENDING = [
  {
    _id: "demo-pending-1",
    createdAt: "2026-03-08T10:24:00.000Z",
    name: "Rahul Sharma",
    whatsapp: "+91 98765 43210",
    alternateContactNumber: "+91 91234 56789",
    theater: "Theater 1 — Grand",
    date: "2026-04-18",
    timeSlot: "06:00 PM – 09:00 PM",
    occasion: "Birthday",
    celebrationName: "Aarav's Birthday",
    adults: 10,
    kids: 0,
    persons: 10,
    packageAmount: 4999,
    addonsAmount: 800,
    otherDiscount: 0,
    managerDiscount: 500,
    advancePaid: 2000,
    total: 6799,
    foodDetails: "Veg combo platter, 2 cookie boxes, 10 mocktails",
    cakeMessage: "Happy Birthday Aarav",
    cakeFlavor: "Chocolate Truffle",
    cakeWeight: 1.5,
    candleNumber: 10,
    sashType: "Birthday Boy",
    crownType: "King",
    managerRemarks: "Repeat customer — keep the premium seating plan.",
    specialInstructions:
      "Setup 30 minutes before the event. Keep the aisle clear for the cake entry.",
    extraPersonDetails: DEMO_EXTRA_DETAILS,
    cakes: [{ name: "Chocolate Truffle Cake", price: 800, qty: 1 }],
    gifts: [],
    extraDecors: [],
    services: [],
    complimentaryItems: [
      "Welcome Drink",
      "Popcorn (Large)",
      "Chocolate Box",
    ],
    customFields: [
      {
        id: "cf-1",
        label: "Balloon Color",
        type: "text",
        required: false,
        options: [],
        value: "Rose Gold",
      },
      {
        id: "cf-2",
        label: "Seating Preference",
        type: "dropdown",
        required: false,
        options: ["Recliner", "Sofa", "Standard"],
        value: "Recliner",
      },
    ],
  },
  {
    _id: "demo-pending-2",
    createdAt: "2026-03-09T06:10:00.000Z",
    name: "Meera Iyer",
    whatsapp: "+91 99887 76655",
    alternateContactNumber: "",
    theater: "Theater 3 — Aurora",
    date: "2026-04-22",
    timeSlot: "12:30 PM – 03:30 PM",
    occasion: "Anniversary",
    celebrationName: "10th Anniversary",
    adults: 12,
    kids: 2,
    persons: 14,
    packageAmount: 5999,
    addonsAmount: 1250,
    managerDiscount: 0,
    advancePaid: 3000,
    total: 7949,
    foodDetails: "Continental platter with 4 desserts",
    cakeMessage: "Happy Anniversary",
    cakeFlavor: "Red Velvet",
    cakeWeight: 2,
    candleNumber: 2,
    sashType: "Not Required",
    crownType: "Queen",
    managerRemarks: "",
    specialInstructions: "Add a photo corner near the entrance.",
    extraPersonDetails: {
      totalPersons: 14,
      packageIncludedPersons: 10,
      complimentaryPersons: 2,
      extraPersons: 4,
      chargeablePersons: 2,
      standardCostPerPerson: 350,
      discountPerPerson: 0,
      finalCostPerPerson: 350,
      extraPersonAmount: 700,
      isActive: true,
    },
    cakes: [{ name: "Red Velvet Cake", price: 1100, qty: 1 }],
    gifts: [{ name: "Gift Hamper", price: 150, qty: 1 }],
    extraDecors: [],
    services: [],
    complimentaryItems: ["Welcome Drink", "Photo Booth"],
    customFields: [],
  },
];

const DEMO_TRANSFERRED = [
  {
    _id: "demo-transferred-1",
    orderId: "demo-order-9",
    customerName: "Karthik Menon",
    name: "Karthik Menon",
    whatsapp: "+91 90011 22334",
    theater: "Theater 2 — Cosmos",
    eventDate: "2026-03-28",
    date: "2026-03-28",
    timeSlot: "07:00 PM – 10:00 PM",
    occasion: "Baby Shower",
    adults: 18,
    kids: 4,
    packageAmount: 8499,
    addonsAmount: 1600,
    managerDiscount: 300,
    amount: 9799,
    total: 9799,
    transferredBy: "Admin",
    transferredAt: "2026-03-05T11:05:00.000Z",
    status: "In Staff Management",
    cakeMessage: "Welcome Little One",
    cakeFlavor: "Butterscotch",
    cakeWeight: 2.5,
    candleNumber: 0,
    sashType: "Not Required",
    crownType: "Not Required",
    complimentaryItems: ["Welcome Drink", "Dessert Platter"],
    customFields: [],
    extraPersonDetails: {
      totalPersons: 22,
      packageIncludedPersons: 15,
      complimentaryPersons: 3,
      extraPersons: 7,
      chargeablePersons: 4,
      standardCostPerPerson: 350,
      discountPerPerson: 50,
      finalCostPerPerson: 300,
      extraPersonAmount: 1200,
      isActive: true,
    },
  },
];

/* ═══════════════════════════════════════════════════════
   CUSTOM FIELDS
   ═══════════════════════════════════════════════════════ */
let customFieldCounter = 0;

const createCustomFieldId = () => {
  if (
    typeof window !== "undefined" &&
    window.crypto &&
    typeof window.crypto.randomUUID === "function"
  ) {
    return window.crypto.randomUUID();
  }

  customFieldCounter += 1;
  return `custom-field-${Date.now()}-${customFieldCounter}`;
};

const CUSTOM_FIELD_TYPES = [
  { value: "text", label: "Normal input" },
  { value: "textarea", label: "Long text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "dropdown", label: "Dropdown" },
  { value: "radio", label: "Radio buttons" },
];

const getRecordValue = (record, keys) => {
  const containers = [
    record,
    record?.handoverDetails,
    record?.extraPersonDetails,
    record?.bookingDetails,
    record?.contactDetails,
    record?.details,
    record?.orderSnapshot,
    record?.snapshot,
    record?.order,
  ].filter(Boolean);

  for (const container of containers) {
    for (const key of keys) {
      const value = container?.[key];
      if (value !== undefined && value !== null) return value;
    }
  }

  return "";
};

const normalizeComplimentaryItems = (value) => {
  let items = [];

  if (Array.isArray(value)) {
    items = value.map((item) => {
      if (typeof item === "string" || typeof item === "number") {
        return String(item);
      }
      return item?.name || item?.label || item?.value || "";
    });
  } else if (value) {
    items = String(value).split(/[\n,]+/);
  }

  const unique = [];
  const used = new Set();

  items.forEach((item) => {
    const clean = trimValue(item);
    const key = clean.toLowerCase();

    if (clean && !used.has(key)) {
      used.add(key);
      unique.push(clean);
    }
  });

  return unique;
};

const normalizeCustomFields = (record) => {
  const rawFields = getRecordValue(record, [
    "customFields",
    "additionalFields",
    "extraFields",
  ]);

  if (!Array.isArray(rawFields)) return [];

  return rawFields.map((field) => {
    const rawType = String(field?.type || "text").toLowerCase();

    const type = CUSTOM_FIELD_TYPES.some((ft) => ft.value === rawType)
      ? rawType
      : "text";

    let options = [];

    if (Array.isArray(field?.options)) {
      options = field.options.map(trimValue).filter(Boolean);
    } else if (field?.options) {
      options = String(field.options)
        .split(",")
        .map(trimValue)
        .filter(Boolean);
    }

    if (["dropdown", "radio"].includes(type) && options.length === 0) {
      options = ["Option 1", "Option 2"];
    }

    return {
      id: field?.id || field?._id || createCustomFieldId(),
      label: trimValue(field?.label || field?.name),
      type,
      required: Boolean(field?.required),
      options,
      value:
        field?.value === undefined || field?.value === null
          ? ""
          : String(field.value),
    };
  });
};

const createEmptyCustomField = () => ({
  id: createCustomFieldId(),
  label: "",
  type: "text",
  required: false,
  options: [],
  value: "",
});

const sanitizeCustomFields = (fields) =>
  fields.map((field) => {
    const hasOptions = ["dropdown", "radio"].includes(field.type);

    return {
      id: field.id || createCustomFieldId(),
      label: trimValue(field.label),
      type: field.type,
      required: Boolean(field.required),
      options: hasOptions ? field.options.map(trimValue).filter(Boolean) : [],
      value: trimValue(field.value),
    };
  });

/* ═══════════════════════════════════════════════════════
   FORM HYDRATION / PAYLOAD / VALIDATION
   ═══════════════════════════════════════════════════════ */
const hydrateEditForm = (record, source) => {
  const customerName = getRecordValue(
    record,
    source === "pending" ? ["name", "customerName"] : ["customerName", "name"]
  );

  const extra =
    record?.extraPersonDetails ||
    getRecordValue(record, ["extraPersonDetails"]) ||
    {};

  return {
    customerName: String(customerName || ""),
    primaryContactNumber: String(
      getRecordValue(record, [
        "primaryContactNumber",
        "whatsapp",
        "contactNumber",
        "phone",
      ]) || ""
    ),
    alternateContactNumber: String(
      getRecordValue(record, [
        "alternateContactNumber",
        "alternateContact",
        "alternatePhone",
      ]) || ""
    ),
    foodDetails: String(
      getRecordValue(record, ["foodDetails", "food"]) || ""
    ),
    cakeMessage: String(
      getRecordValue(record, ["cakeMessage", "textOnCake"]) || ""
    ),
    cakeFlavor: String(
      getRecordValue(record, ["cakeFlavor", "cakeFlavour"]) || ""
    ),
    cakeWeight: String(getRecordValue(record, ["cakeWeight"]) || ""),
    candleNumber: String(
      getRecordValue(record, ["candleNumber", "numberOfCandles"]) || ""
    ),
    sashType: String(getRecordValue(record, ["sashType"]) || ""),
    crownType: String(getRecordValue(record, ["crownType"]) || ""),
   
    managerDiscount: String(
      getRecordValue(record, ["managerDiscount", "discount"]) || ""
    ),
    managerRemarks: String(getRecordValue(record, ["managerRemarks"]) || ""),
    specialInstructions: String(
      getRecordValue(record, ["specialInstructions", "instructions"]) || ""
    ),
    /* ★ extra person block (includes the complimentary persons) */
    totalPersons:
      extra?.totalPersons === undefined || extra?.totalPersons === null
        ? ""
        : String(extra.totalPersons),
    packageIncludedPersons: String(extra?.packageIncludedPersons ?? ""),
    complimentaryPersons: String(extra?.complimentaryPersons ?? ""),
    standardCostPerPerson: String(extra?.standardCostPerPerson ?? ""),
    discountPerPerson: String(extra?.discountPerPerson ?? ""),
    extraPersonRemarks: String(extra?.remarks ?? ""),
    complimentaryItems: normalizeComplimentaryItems(
      getRecordValue(record, ["complimentaryItems", "complimentary"])
    ),
    customFields: normalizeCustomFields(record),
  };
};

const buildExtraPersonPayload = (form) => {
  const pricing = computeExtraPersonPricing(form);

  return {
    ...pricing,
    remarks: trimValue(form.extraPersonRemarks),
  };
};

const buildUpdatePayload = (form, source, record) => {
  const extraPersonDetails = buildExtraPersonPayload(form);
  const pricing = computeExtraPersonPricing(form);

  const commonPayload = {
    primaryContactNumber: trimValue(form.primaryContactNumber),
    alternateContactNumber: trimValue(form.alternateContactNumber),
    foodDetails: trimValue(form.foodDetails),
    cakeMessage: trimValue(form.cakeMessage),
    cakeFlavor: trimValue(form.cakeFlavor),
    cakeWeight: form.cakeWeight === "" ? null : Number(form.cakeWeight),
    candleNumber:
      form.candleNumber === "" ? null : Number(form.candleNumber),
    sashType: trimValue(form.sashType),
    crownType: trimValue(form.crownType),
    managerDiscount:
      form.managerDiscount === "" ? null : Number(form.managerDiscount),
    managerRemarks: trimValue(form.managerRemarks),
    specialInstructions: trimValue(form.specialInstructions),
    extraPersonDetails,
    extraPersonAmount: extraPersonDetails.extraPersonAmount,
    addonsAmount: addonsAmountOf(record),
    packageAmount: packageAmountOf(record),
    finalAmount:
      packageAmountOf(record) +
      pricing.extraPersonAmount +
      addonsAmountOf(record) -
      toAmount(form.managerDiscount) -
      firstFiniteNumber(record, ["otherDiscount", "extraDiscount"]),
    complimentaryItems: form.complimentaryItems.map(trimValue).filter(Boolean),
    customFields: sanitizeCustomFields(form.customFields),
  };

  if (source === "pending") {
    return {
      ...commonPayload,
      name: trimValue(form.customerName),
      customerName: trimValue(form.customerName),
      whatsapp: trimValue(form.primaryContactNumber),
    };
  }

  return {
    ...commonPayload,
    customerName: trimValue(form.customerName),
    name: trimValue(form.customerName),
    whatsapp: trimValue(form.primaryContactNumber),
  };
};

const validateEditForm = (form, target) => {
  const errors = {};

  if (!trimValue(form.customerName)) {
    errors.customerName = "Customer name is required.";
  }

  const primaryDigits = String(form.primaryContactNumber || "").replace(
    /\D/g,
    ""
  );

  if (!primaryDigits) {
    errors.primaryContactNumber = "Primary contact number is required.";
  } else if (primaryDigits.length < 7 || primaryDigits.length > 15) {
    errors.primaryContactNumber =
      "Enter a valid contact number containing 7 to 15 digits.";
  }

  const alternateDigits = String(form.alternateContactNumber || "").replace(
    /\D/g,
    ""
  );

  if (alternateDigits && (alternateDigits.length < 7 || alternateDigits.length > 15)) {
    errors.alternateContactNumber =
      "Enter a valid alternate number containing 7 to 15 digits.";
  }

  if (alternateDigits && primaryDigits && alternateDigits === primaryDigits) {
    errors.alternateContactNumber =
      "Alternate contact must be different from the primary contact.";
  }

  if (form.cakeWeight !== "") {
    const weight = Number(form.cakeWeight);
    if (!Number.isFinite(weight) || weight <= 0) {
      errors.cakeWeight = "Cake weight must be greater than zero.";
    }
  }

  if (form.candleNumber !== "") {
    const candleNumber = Number(form.candleNumber);
    if (!Number.isInteger(candleNumber) || candleNumber < 0) {
      errors.candleNumber = "Candle number must be a positive whole number.";
    }
  }

  if (form.managerDiscount !== "") {
    const discount = Number(form.managerDiscount);
    const bookingAmount = n(target?.total || target?.amount);

    if (!Number.isFinite(discount) || discount < 0) {
      errors.managerDiscount = "Manager discount cannot be negative.";
    } else if (bookingAmount > 0 && discount > bookingAmount) {
      errors.managerDiscount =
        "Manager discount cannot exceed the booking amount.";
    }
  }

  /* ── extra person validation ── */
  const pricing = computeExtraPersonPricing(form);

  if (
    String(form.totalPersons || "") !== "" &&
    !Number.isFinite(Number(form.totalPersons))
  ) {
    errors.totalPersons = "Enter a valid number of persons.";
  }

  if (pricing.packageIncludedPersons > pricing.totalPersons) {
    errors.packageIncludedPersons =
      "Package included persons cannot be more than the total persons.";
  }

  if (toCount(form.complimentaryPersons) > pricing.extraPersons) {
    errors.complimentaryPersons =
      "Complimentary persons cannot be more than the extra persons.";
  }

  if (
    String(form.complimentaryPersons || "") !== "" &&
    !Number.isFinite(Number(form.complimentaryPersons))
  ) {
    errors.complimentaryPersons =
      "Enter a valid number of complimentary persons.";
  }

  if (toAmount(form.discountPerPerson) > toAmount(form.standardCostPerPerson)) {
    errors.discountPerPerson =
      "Per person discount cannot exceed the standard cost per person.";
  }

  /* ── custom fields validation ── */
  const usedLabels = new Map();

  form.customFields.forEach((field, index) => {
    const label = trimValue(field.label);
    const labelKey = label.toLowerCase();
    const fieldPrefix = `custom_${index}`;

    if (!label) {
      errors[`${fieldPrefix}_label`] = "Custom field label is required.";
    } else if (usedLabels.has(labelKey)) {
      errors[`${fieldPrefix}_label`] = "Custom field labels must be unique.";
      errors[`custom_${usedLabels.get(labelKey)}_label`] =
        "Custom field labels must be unique.";
    } else {
      usedLabels.set(labelKey, index);
    }

    if (["dropdown", "radio"].includes(field.type)) {
      const options = field.options.map(trimValue).filter(Boolean);
      const uniqueOptions = new Set(options.map((o) => o.toLowerCase()));

      if (options.length < 2) {
        errors[`${fieldPrefix}_options`] = "Add at least two options.";
      } else if (uniqueOptions.size !== options.length) {
        errors[`${fieldPrefix}_options`] = "Options must be unique.";
      }

      if (trimValue(field.value) && !options.includes(trimValue(field.value))) {
        errors[`${fieldPrefix}_value`] =
          "Select one of the available options.";
      }
    }

    if (field.required && !trimValue(field.value)) {
      errors[`${fieldPrefix}_value`] = "A value is required for this field.";
    }

    if (
      field.type === "number" &&
      trimValue(field.value) &&
      !Number.isFinite(Number(field.value))
    ) {
      errors[`${fieldPrefix}_value`] = "Enter a valid number.";
    }
  });

  return errors;
};

const extractUpdatedRecord = (result, source) => {
  if (!result || typeof result !== "object") return null;

  const candidates =
    source === "pending"
      ? [
          result.order,
          result.updatedOrder,
          result.data?.order,
          result.data?.updatedOrder,
          result.data,
        ]
      : [
          result.transfer,
          result.updatedTransfer,
          result.data?.transfer,
          result.data?.updatedTransfer,
          result.data,
        ];

  return (
    candidates.find(
      (c) => c && typeof c === "object" && !Array.isArray(c)
    ) || null
  );
};

const computeBookingSummary = (record, form) => {
  const packageAmount = packageAmountOf(record);
  const addonsAmount = addonsAmountOf(record);
  const pricing = computeExtraPersonPricing(form);

  const managerDiscount =
    form?.managerDiscount !== undefined
      ? toAmount(form.managerDiscount)
      : firstFiniteNumber(record, ["managerDiscount"]);

  const discounts =
    managerDiscount + firstFiniteNumber(record, ["otherDiscount", "extraDiscount"]);

  return {
    packageAmount,
    addonAmount: addonsAmount,
    extraPersonAmount: pricing.extraPersonAmount,
    discounts,
    finalTotal: Math.max(
      0,
      packageAmount + pricing.extraPersonAmount + addonsAmount - discounts
    ),
  };
};

/* ═══════════════════════════════════════════════════════
   SMALL COMPONENTS
   ═══════════════════════════════════════════════════════ */
const FieldError = ({ message }) => {
  if (!message) return null;

  return (
    <small className="tc-field-error">
      <span>⚠</span> {message}
    </small>
  );
};

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = window.setTimeout(onClose, 4000);
    return () => window.clearTimeout(timer);
  }, [message, type, onClose]);

  const icons = {
    success: "✅",
    error: "❌",
    info: "ℹ️",
    warning: "⚠️",
  };

  return (
    <div
      className={`tc-toast tc-toast-${type}`}
      role="status"
      aria-live="polite"
    >
      <span className="tc-toast-icon">{icons[type] || icons.info}</span>
      <span className="tc-toast-msg">{message}</span>
      <button
        type="button"
        className="tc-toast-close"
        onClick={onClose}
        aria-label="Close notification"
      >
        ✕
      </button>
    </div>
  );
};

const ConfirmModal = ({
  open,
  title,
  description,
  details,
  warningText,
  confirmLabel,
  confirmColor,
  isProcessing,
  processingLabel,
  error,
  success,
  onConfirm,
  onCancel,
}) => {
  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !isProcessing) onCancel();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, isProcessing, onCancel]);

  if (!open) return null;

  return (
    <div
      className="tc-modal-overlay"
      onMouseDown={() => {
        if (!isProcessing) onCancel();
      }}
    >
      <div
        className="tc-modal-card"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="tc-modal-header">
          <h3>{title}</h3>
          <button
            type="button"
            className="tc-modal-close-btn"
            onClick={onCancel}
            disabled={isProcessing}
            aria-label="Close modal"
          >
            ✕
          </button>
        </div>

        <p className="tc-modal-desc">{description}</p>

        {warningText && (
          <div className="tc-modal-warning">
            <span>⚠️</span>
            <span>{warningText}</span>
          </div>
        )}

        {details && <div className="tc-modal-details">{details}</div>}

        {error && <div className="tc-modal-error">⚠️ {error}</div>}
        {success && <div className="tc-modal-success">✅ {success}</div>}

        <div className="tc-modal-actions">
          <button
            type="button"
            className="tc-btn-secondary"
            onClick={onCancel}
            disabled={isProcessing}
          >
            Cancel
          </button>
          <button
            type="button"
            className="tc-btn-primary"
            style={{ background: confirmColor || "#4f46e5" }}
            onClick={onConfirm}
            disabled={isProcessing}
          >
            {isProcessing ? processingLabel || "Processing..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════ */
const TransferContact = () => {
  const [orders, setOrders] = useState([]);
  const [transferred, setTransferred] = useState([]);
  const [loading, setLoading] = useState(true);
  const [demoMode, setDemoMode] = useState(false);

  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [sortKey, setSortKey] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");
  const [activeTab, setActiveTab] = useState("pending");

  const [toast, setToast] = useState(null);

  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isTransferring, setIsTransferring] = useState(false);
  const [transferError, setTransferError] = useState("");
  const [transferSuccess, setTransferSuccess] = useState("");

  const [deletePendingModalOpen, setDeletePendingModalOpen] = useState(false);
  const [deletePendingTarget, setDeletePendingTarget] = useState(null);
  const [isDeletingPending, setIsDeletingPending] = useState(false);
  const [deletePendingError, setDeletePendingError] = useState("");
  const [deletePendingSuccess, setDeletePendingSuccess] = useState("");

  const [deleteTransferModalOpen, setDeleteTransferModalOpen] =
    useState(false);
  const [deleteTransferTarget, setDeleteTransferTarget] = useState(null);
  const [isDeletingTransfer, setIsDeletingTransfer] = useState(false);
  const [deleteTransferError, setDeleteTransferError] = useState("");
  const [deleteTransferSuccess, setDeleteTransferSuccess] = useState("");

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editSource, setEditSource] = useState("pending");
  const [editForm, setEditForm] = useState(() =>
    hydrateEditForm({}, "pending")
  );
  const [editInitialSnapshot, setEditInitialSnapshot] = useState("");
  const [editFieldErrors, setEditFieldErrors] = useState({});
  const [editRequestError, setEditRequestError] = useState("");
  const [editSuccess, setEditSuccess] = useState("");
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [complimentaryDraft, setComplimentaryDraft] = useState("");

  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
  }, []);

  const closeToast = useCallback(() => setToast(null), []);

  /* ─────────────────────────────────────────────────────
     FETCH
     ───────────────────────────────────────────────────── */
  const fetchPendingOrders = useCallback(async () => {
    try {
      const response = await fetchWithTimeout(
        `${PENDING_ORDERS_API}?limit=500`,
        {},
        6000
      );

      const result = await readResponseBody(response);

      if (!response.ok) {
        throw new Error(result.message || "Failed to fetch pending orders");
      }

      const data = Array.isArray(result.data)
        ? result.data
        : Array.isArray(result)
        ? result
        : [];

      const activeOrders = data.filter(
        (order) =>
          order.status !== "Cancelled" &&
          order.status !== "Transferred to Staff" &&
          !order.isTransferredToStaff
      );

      setOrders(activeOrders);
      setDemoMode(false);
      return true;
    } catch (error) {
      console.error("Fetch pending error:", error);

      try {
        const response = await fetchWithTimeout(
          `${ORDERS_API}?limit=500`,
          {},
          6000
        );
        const result = await readResponseBody(response);

        if (response.ok) {
          const data = Array.isArray(result.data)
            ? result.data
            : Array.isArray(result)
            ? result
            : [];

          setOrders(
            data.filter(
              (order) =>
                order.status !== "Cancelled" &&
                order.status !== "Transferred to Staff" &&
                !order.isTransferredToStaff
            )
          );
          setDemoMode(false);
          return true;
        }
      } catch (fallbackError) {
        console.error("Fallback fetch pending error:", fallbackError);
      }

      /* offline demo fallback so the UI stays explorable */
      setDemoMode(true);
      setOrders((prev) => (prev.length ? prev : DEMO_PENDING));
      return false;
    }
  }, []);

  const fetchTransferredList = useCallback(async () => {
    try {
      const response = await fetchWithTimeout(TRANSFER_LIST_API, {}, 6000);
      const result = await readResponseBody(response);

      if (!response.ok) {
        throw new Error(
          result.message || "Failed to fetch transferred bookings"
        );
      }

      const data = Array.isArray(result.data)
        ? result.data
        : Array.isArray(result)
        ? result
        : [];

      setTransferred(data);
      return true;
    } catch (error) {
      console.error("Fetch transferred error:", error);
      setDemoMode(true);
      setTransferred((prev) => (prev.length ? prev : DEMO_TRANSFERRED));
      return false;
    }
  }, []);

  useEffect(() => {
    let active = true;

    const loadData = async () => {
      setLoading(true);

      const [pendingOk] = await Promise.all([
        fetchPendingOrders(),
        fetchTransferredList(),
      ]);

      if (!active) return;

      setLoading(false);

      if (!pendingOk) {
        showToast(
          "Backend API unreachable — showing sample bookings in demo mode.",
          "info"
        );
      }
    };

    loadData();

    return () => {
      active = false;
    };
  }, [fetchPendingOrders, fetchTransferredList, showToast]);

  useEffect(() => {
    const handleFocus = () => {
      if (!demoMode) {
        fetchPendingOrders();
        fetchTransferredList();
      }
    };

    window.addEventListener("focus", handleFocus);

    return () => window.removeEventListener("focus", handleFocus);
  }, [fetchPendingOrders, fetchTransferredList, demoMode]);

  const anyModalOpen =
    transferModalOpen ||
    deletePendingModalOpen ||
    deleteTransferModalOpen ||
    editModalOpen;

  useEffect(() => {
    if (!anyModalOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [anyModalOpen]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const filteredPending = useMemo(() => {
    let rows = [...orders];

    if (fromDate || toDate) {
      rows = rows.filter((order) => {
        const rowDate = parseDateToYYYYMMDD(order.date);
        if (!rowDate) return false;
        if (fromDate && rowDate < fromDate) return false;
        if (toDate && rowDate > toDate) return false;
        return true;
      });
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((order) =>
        [
          order.name,
          order.customerName,
          order.theater,
          order.whatsapp,
          order.primaryContactNumber,
          order.alternateContactNumber,
          order.occasion,
          order.date,
          order.managerRemarks,
        ].some((v) => String(v || "").toLowerCase().includes(q))
      );
    }

    rows.sort((a, b) => {
      let av = a[sortKey] ?? "";
      let bv = b[sortKey] ?? "";

      if (sortKey === "total") {
        const at = firstFiniteNumber(a, ["total", "amount"]);
        const bt = firstFiniteNumber(b, ["total", "amount"]);
        return sortDir === "asc" ? at - bt : bt - at;
      }

      if (sortKey === "createdAt") {
        av = new Date(av).getTime() || 0;
        bv = new Date(bv).getTime() || 0;
        return sortDir === "asc" ? av - bv : bv - av;
      }

      return sortDir === "asc"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });

    return rows;
  }, [orders, search, fromDate, toDate, sortKey, sortDir]);

  const filteredTransferred = useMemo(() => {
    let rows = [...transferred];

    if (fromDate || toDate) {
      rows = rows.filter((t) => {
        const rowDate = parseDateToYYYYMMDD(t.eventDate || t.date);
        if (!rowDate) return false;
        if (fromDate && rowDate < fromDate) return false;
        if (toDate && rowDate > toDate) return false;
        return true;
      });
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((t) =>
        [
          t.customerName,
          t.name,
          t.theater,
          t.whatsapp,
          t.primaryContactNumber,
          t.alternateContactNumber,
          t.occasion,
          t.eventDate,
          t.managerRemarks,
        ].some((v) => String(v || "").toLowerCase().includes(q))
      );
    }

    return rows;
  }, [transferred, search, fromDate, toDate]);

  /* ─────────────────────────────────────────────────────
     TRANSFER
     ───────────────────────────────────────────────────── */
  const openTransferModal = (order) => {
    setSelectedOrder(order);
    setTransferError("");
    setTransferSuccess("");
    setTransferModalOpen(true);
  };

  const closeTransferModal = () => {
    if (isTransferring) return;
    setTransferModalOpen(false);
    setSelectedOrder(null);
    setTransferError("");
    setTransferSuccess("");
  };

  const handleTransferClick = async () => {
    if (!selectedOrder) return;

    setIsTransferring(true);
    setTransferError("");

    try {
      if (demoMode) {
        await new Promise((resolve) => window.setTimeout(resolve, 500));

        const pricing = computeExtraPersonPricing(
          hydrateEditForm(selectedOrder, "pending")
        );
        const summary = computeBookingSummary(selectedOrder, {
          ...hydrateEditForm(selectedOrder, "pending"),
        });

        const newTransfer = {
          ...selectedOrder,
          _id: `demo-transferred-${Date.now()}`,
          customerName: selectedOrder.name || selectedOrder.customerName,
          whatsapp:
            selectedOrder.whatsapp || selectedOrder.primaryContactNumber,
          eventDate: selectedOrder.date,
          amount: summary.finalTotal,
          finalAmount: summary.finalTotal,
          extraPersonDetails: pricing,
          transferredAt: new Date().toISOString(),
          transferredBy: "Admin",
          status: "In Staff Management",
        };

        setOrders((prev) =>
          prev.filter((o) => o._id !== selectedOrder._id)
        );
        setTransferred((prev) => [newTransfer, ...prev]);
      } else {
        const response = await fetchWithTimeout(TRANSFER_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ orderId: selectedOrder._id }),
        });

        const result = await readResponseBody(response);

        if (!response.ok) {
          throw new Error(result.message || "Transfer failed");
        }

        setOrders((prev) => prev.filter((o) => o._id !== selectedOrder._id));

        const newTransfer =
          result.transfer || result.data?.transfer || result.data;

        if (
          newTransfer &&
          typeof newTransfer === "object" &&
          !Array.isArray(newTransfer)
        ) {
          setTransferred((prev) => [
            newTransfer,
            ...prev.filter((item) => item._id !== newTransfer._id),
          ]);
        } else {
          await fetchTransferredList();
        }
      }

      setTransferSuccess(
        `${selectedOrder.name} transferred successfully!`
      );

      showToast(
        `${selectedOrder.name} transferred to Staff Management`,
        "success"
      );

      window.setTimeout(() => {
        setTransferModalOpen(false);
        setSelectedOrder(null);
        setTransferError("");
        setTransferSuccess("");
      }, 1200);
    } catch (error) {
      setTransferError(
        error.name === "AbortError"
          ? "The transfer request timed out."
          : error.message
      );
    } finally {
      setIsTransferring(false);
    }
  };

  /* ─────────────────────────────────────────────────────
     DELETE PENDING
     ───────────────────────────────────────────────────── */
  const openDeletePendingModal = (order) => {
    setDeletePendingTarget(order);
    setDeletePendingError("");
    setDeletePendingSuccess("");
    setDeletePendingModalOpen(true);
  };

  const closeDeletePendingModal = () => {
    if (isDeletingPending) return;
    setDeletePendingModalOpen(false);
    setDeletePendingTarget(null);
    setDeletePendingError("");
    setDeletePendingSuccess("");
  };

  const handleDeletePendingConfirm = async () => {
    if (!deletePendingTarget) return;

    setIsDeletingPending(true);
    setDeletePendingError("");

    try {
      if (demoMode) {
        await new Promise((resolve) => window.setTimeout(resolve, 400));
      } else {
        const response = await fetchWithTimeout(
          `${ORDER_DELETE_API}/${deletePendingTarget._id}`,
          { method: "DELETE", headers: { "Content-Type": "application/json" } }
        );

        const result = await readResponseBody(response);

        if (!response.ok) {
          throw new Error(result.message || "Delete failed");
        }
      }

      setOrders((prev) =>
        prev.filter((o) => o._id !== deletePendingTarget._id)
      );

      setDeletePendingSuccess("Order permanently deleted!");

      showToast(
        `${deletePendingTarget.name || "Order"} permanently deleted`,
        "success"
      );

      window.setTimeout(() => {
        setDeletePendingModalOpen(false);
        setDeletePendingTarget(null);
        setDeletePendingError("");
        setDeletePendingSuccess("");
      }, 1000);
    } catch (error) {
      const msg =
        error.name === "AbortError"
          ? "Delete request timed out."
          : error.message;
      setDeletePendingError(msg);
      showToast(`Delete failed: ${msg}`, "error");
    } finally {
      setIsDeletingPending(false);
    }
  };

  /* ─────────────────────────────────────────────────────
     DELETE TRANSFER
     ───────────────────────────────────────────────────── */
  const openDeleteTransferModal = (transfer) => {
    setDeleteTransferTarget(transfer);
    setDeleteTransferError("");
    setDeleteTransferSuccess("");
    setDeleteTransferModalOpen(true);
  };

  const closeDeleteTransferModal = () => {
    if (isDeletingTransfer) return;
    setDeleteTransferModalOpen(false);
    setDeleteTransferTarget(null);
    setDeleteTransferError("");
    setDeleteTransferSuccess("");
  };

  const handleDeleteTransferConfirm = async () => {
    if (!deleteTransferTarget) return;

    setIsDeletingTransfer(true);
    setDeleteTransferError("");

    try {
      if (demoMode) {
        await new Promise((resolve) => window.setTimeout(resolve, 400));
      } else {
        const response = await fetchWithTimeout(
          `${TRANSFER_DELETE_API}/${deleteTransferTarget._id}`,
          { method: "DELETE", headers: { "Content-Type": "application/json" } }
        );

        const result = await readResponseBody(response);

        if (!response.ok) throw new Error(result.message || "Delete failed");
      }

      setTransferred((prev) =>
        prev.filter((t) => t._id !== deleteTransferTarget._id)
      );

      setDeleteTransferSuccess("Record permanently deleted!");

      showToast(
        `${deleteTransferTarget.customerName || "Record"} permanently deleted`,
        "success"
      );

      window.setTimeout(() => {
        setDeleteTransferModalOpen(false);
        setDeleteTransferTarget(null);
        setDeleteTransferError("");
        setDeleteTransferSuccess("");
      }, 1000);

      if (!demoMode) fetchPendingOrders();
    } catch (error) {
      const msg =
        error.name === "AbortError"
          ? "Delete request timed out."
          : error.message;
      setDeleteTransferError(msg);
      showToast(`Delete failed: ${msg}`, "error");
    } finally {
      setIsDeletingTransfer(false);
    }
  };

  /* ─────────────────────────────────────────────────────
     ADVANCED EDITING
     ───────────────────────────────────────────────────── */
  const openEditModal = (record, source) => {
    const hydratedForm = hydrateEditForm(record, source);

    setEditTarget(record);
    setEditSource(source);
    setEditForm(hydratedForm);
    setEditInitialSnapshot(JSON.stringify(hydratedForm));
    setEditFieldErrors({});
    setEditRequestError("");
    setEditSuccess("");
    setComplimentaryDraft("");
    setEditModalOpen(true);
  };

  const closeEditModal = (force = false) => {
    if (isSavingEdit) return;

    const hasUnsavedChanges =
      JSON.stringify(editForm) !== editInitialSnapshot ||
      Boolean(complimentaryDraft.trim());

    if (
      !force &&
      !editSuccess &&
      hasUnsavedChanges &&
      !window.confirm(
        "You have unsaved changes. Do you want to close the editor?"
      )
    ) {
      return;
    }

    setEditModalOpen(false);
    setEditTarget(null);
    setEditFieldErrors({});
    setEditRequestError("");
    setEditSuccess("");
    setComplimentaryDraft("");
  };

  const clearEditError = (fieldName) => {
    setEditFieldErrors((prev) => {
      if (!prev[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  };

  const updateEditField = (fieldName, value) => {
    setEditForm((prev) => ({ ...prev, [fieldName]: value }));
    clearEditError(fieldName);
    setEditRequestError("");
    setEditSuccess("");
  };

  const addComplimentaryItems = (rawValue = complimentaryDraft) => {
    const incomingItems = String(rawValue || "")
      .split(/[\n,]+/)
      .map(trimValue)
      .filter(Boolean);

    if (incomingItems.length === 0) return;

    setEditForm((prev) => {
      const existing = new Set(
        prev.complimentaryItems.map((i) => i.toLowerCase())
      );

      const additions = incomingItems.filter((item) => {
        const key = item.toLowerCase();
        if (existing.has(key)) return false;
        existing.add(key);
        return true;
      });

      return {
        ...prev,
        complimentaryItems: [...prev.complimentaryItems, ...additions],
      };
    });

    setComplimentaryDraft("");
  };

  const removeComplimentaryItem = (index) => {
    setEditForm((prev) => ({
      ...prev,
      complimentaryItems: prev.complimentaryItems.filter(
        (_, i) => i !== index
      ),
    }));
  };

  const addCustomField = () => {
    setEditForm((prev) => ({
      ...prev,
      customFields: [...prev.customFields, createEmptyCustomField()],
    }));
    setEditFieldErrors({});
    setEditRequestError("");
  };

  const removeCustomField = (index) => {
    setEditForm((prev) => ({
      ...prev,
      customFields: prev.customFields.filter((_, i) => i !== index),
    }));
    setEditFieldErrors({});
    setEditRequestError("");
  };

  const updateCustomField = (index, changes) => {
    setEditForm((prev) => ({
      ...prev,
      customFields: prev.customFields.map((field, i) =>
        i === index ? { ...field, ...changes } : field
      ),
    }));
    setEditFieldErrors({});
    setEditRequestError("");
    setEditSuccess("");
  };

  const handleCustomFieldTypeChange = (index, nextType) => {
    const currentField = editForm.customFields[index];
    if (!currentField) return;

    let options = currentField.options;

    if (["dropdown", "radio"].includes(nextType) && options.length < 2) {
      options = ["Option 1", "Option 2"];
    }

    updateCustomField(index, { type: nextType, options, value: "" });
  };

  const addCustomOption = (fieldIndex) => {
    const field = editForm.customFields[fieldIndex];
    if (!field) return;

    updateCustomField(fieldIndex, {
      options: [...field.options, `Option ${field.options.length + 1}`],
    });
  };

  const updateCustomOption = (fieldIndex, optionIndex, value) => {
    const field = editForm.customFields[fieldIndex];
    if (!field) return;

    const oldOption = field.options[optionIndex];

    const options = field.options.map((opt, i) =>
      i === optionIndex ? value : opt
    );

    updateCustomField(fieldIndex, {
      options,
      value: field.value === oldOption ? value : field.value,
    });
  };

  const removeCustomOption = (fieldIndex, optionIndex) => {
    const field = editForm.customFields[fieldIndex];
    if (!field) return;

    const removedOption = field.options[optionIndex];

    updateCustomField(fieldIndex, {
      options: field.options.filter((_, i) => i !== optionIndex),
      value: field.value === removedOption ? "" : field.value,
    });
  };

  const handleSaveEdit = async (event) => {
    event.preventDefault();

    if (!editTarget || isSavingEdit) return;

    let formToSave = editForm;

    if (complimentaryDraft.trim()) {
      const draftItems = String(complimentaryDraft)
        .split(/[\n,]+/)
        .map(trimValue)
        .filter(Boolean);

      const existingItems = new Set(
        editForm.complimentaryItems.map((i) => i.toLowerCase())
      );

      const additions = draftItems.filter((item) => {
        const key = item.toLowerCase();
        if (existingItems.has(key)) return false;
        existingItems.add(key);
        return true;
      });

      formToSave = {
        ...editForm,
        complimentaryItems: [...editForm.complimentaryItems, ...additions],
      };

      setEditForm(formToSave);
      setComplimentaryDraft("");
    }

    const validationErrors = validateEditForm(formToSave, editTarget);

    if (Object.keys(validationErrors).length > 0) {
      setEditFieldErrors(validationErrors);
      setEditRequestError(
        "Please correct the highlighted fields before saving."
      );
      window.setTimeout(() => {
        document.querySelector(".tc-input-error")?.focus?.();
      }, 50);
      return;
    }

    if (!editTarget._id) {
      setEditRequestError("This record does not contain a valid ID.");
      return;
    }

    setIsSavingEdit(true);
    setEditFieldErrors({});
    setEditRequestError("");
    setEditSuccess("");

    try {
      const payload = buildUpdatePayload(formToSave, editSource, editTarget);

      const updateUrl =
        editSource === "pending"
          ? getOrderUpdateApi(editTarget._id)
          : getTransferUpdateApi(editTarget._id);

      let serverRecord = null;

      if (demoMode) {
        await new Promise((resolve) => window.setTimeout(resolve, 600));
      } else {
        const result = await requestUpdate(updateUrl, payload);
        serverRecord = extractUpdatedRecord(result, editSource);
      }

      const updatedRecord = {
        ...editTarget,
        ...payload,
        ...(serverRecord || {}),
      };

      if (editSource === "pending") {
        setOrders((prev) =>
          prev.map((o) => (o._id === editTarget._id ? updatedRecord : o))
        );
      } else {
        setTransferred((prev) =>
          prev.map((t) => (t._id === editTarget._id ? updatedRecord : t))
        );
      }

      setEditTarget(updatedRecord);
      setEditInitialSnapshot(JSON.stringify(formToSave));
      setEditSuccess("Booking details updated successfully.");

      showToast(
        `${formToSave.customerName || "Booking"} updated successfully`,
        "success"
      );

      window.setTimeout(() => closeEditModal(true), 1000);
    } catch (error) {
      setEditRequestError(
        error.message || "Unable to update booking details."
      );
      showToast(
        `Update failed: ${error.message || "Unknown error"}`,
        "error"
      );
    } finally {
      setIsSavingEdit(false);
    }
  };

  /* ─────────────────────────────────────────────────────
     DERIVED EDIT VALUES
     ───────────────────────────────────────────────────── */
  const livePricing = useMemo(
    () => computeExtraPersonPricing(editForm),
    [editForm]
  );

  const liveSummary = useMemo(
    () =>
      computeBookingSummary(editTarget || {}, editForm),
    [editTarget, editForm]
  );

  const extraBreakdown =
    livePricing.chargeablePersons === 0
      ? "No chargeable extra persons"
      : `${livePricing.chargeablePersons} × ${money(
          livePricing.finalCostPerPerson
        ).replace("₹", "")} = ${money(livePricing.extraPersonAmount)}`;

  /* ─────────────────────────────────────────────────────
     DETAIL RENDERERS
     ───────────────────────────────────────────────────── */
  const renderAddonDetails = (order) => {
    const details = formatAddonDetails(order);

    if (!details || details.all.length === 0) return null;

    const AddonGroup = ({ icon, title, items, showQty = false }) => {
      if (!items || items.length === 0) return null;

      return (
        <div className="tc-addon-modal-group">
          <div className="tc-addon-modal-title">
            <span>{icon}</span>
            <b>{title}</b>
          </div>
          <div className="tc-addon-modal-list">
            {items.map((item, idx) => (
              <div key={`${item.name}-${idx}`} className="tc-addon-modal-row">
                <span className="tc-addon-modal-name">
                  {item.name}
                  {showQty && (
                    <small className="tc-addon-modal-qty">
                      {qtyNosLabel(item)}
                    </small>
                  )}
                </span>
                <strong className="tc-addon-modal-price">
                  {money(addonDisplayPrice(item))}
                </strong>
              </div>
            ))}
          </div>
        </div>
      );
    };

    return (
      <div className="tc-addon-modal-summary">
        <div className="tc-addon-modal-divider" />
        <h4 className="tc-addon-modal-heading">Add-ons Summary</h4>
        <AddonGroup icon="🎁" title="Gifts" items={details.gifts} showQty />
        <AddonGroup icon="🎂" title="Cakes" items={details.cakes} />
        <AddonGroup icon="🎨" title="Decorations" items={details.decors} />
        <AddonGroup icon="🛎️" title="Services" items={details.services} />
      </div>
    );
  };

  const renderAdditionalDetails = (record) => {
    if (!record) return null;

    const managerDiscount = getRecordValue(record, [
      "managerDiscount",
      "discount",
    ]);
    const cakeWeight = getRecordValue(record, ["cakeWeight"]);
    const extra = record.extraPersonDetails || {};

    const additionalRows = [
      {
        label: "Alternate Contact",
        value: getRecordValue(record, [
          "alternateContactNumber",
          "alternateContact",
          "alternatePhone",
        ]),
      },
      {
        label: "Food Details",
        value: getRecordValue(record, ["foodDetails", "food"]),
      },
      {
        label: "Cake Message",
        value: getRecordValue(record, ["cakeMessage", "textOnCake"]),
      },
      {
        label: "Cake Flavor",
        value: getRecordValue(record, ["cakeFlavor", "cakeFlavour"]),
      },
      {
        label: "Cake Weight",
        value:
          cakeWeight !== "" && cakeWeight !== null
            ? `${cakeWeight} kg`
            : "",
      },
      {
        label: "Candle Number",
        value: getRecordValue(record, ["candleNumber", "numberOfCandles"]),
      },
      
     
      {
        label: "Total / Chargeable Persons",
        value:
          extra.totalPersons || extra.chargeablePersons
            ? `${toCount(extra.totalPersons)} total · ${toCount(
                extra.chargeablePersons
              )} chargeable · ${toCount(
                extra.complimentaryPersons
              )} complimentary`
            : "",
      },
      {
        label: "Extra Person Amount",
        value: extra.extraPersonAmount
          ? money(extra.extraPersonAmount)
          : "",
      },
      {
        label: "Manager Discount",
        value:
          managerDiscount !== "" && managerDiscount !== null
            ? money(managerDiscount)
            : "",
      },
      {
        label: "Manager Remarks",
        value: getRecordValue(record, ["managerRemarks"]),
      },
      {
        label: "Special Instructions",
        value: getRecordValue(record, ["specialInstructions", "instructions"]),
      },
    ].filter(
      (row) => row.value !== "" && row.value !== null && row.value !== undefined
    );

    const complimentaryItems = normalizeComplimentaryItems(
      getRecordValue(record, ["complimentaryItems", "complimentary"])
    );

    const customFields = normalizeCustomFields(record).filter(
      (f) => trimValue(f.label) || trimValue(f.value)
    );

    if (
      additionalRows.length === 0 &&
      complimentaryItems.length === 0 &&
      customFields.length === 0
    ) {
      return null;
    }

    return (
      <div className="tc-extra-summary">
        <div className="tc-addon-modal-divider" />
        <h4 className="tc-addon-modal-heading">Staff Handover Details</h4>

        {additionalRows.map((row) => (
          <div className="tc-extra-summary-row" key={row.label}>
            <span>{row.label}</span>
            <strong>{String(row.value)}</strong>
          </div>
        ))}

        {complimentaryItems.length > 0 && (
          <div className="tc-extra-summary-block">
            <span className="tc-extra-summary-label">Complimentary Items</span>
            <div className="tc-summary-tags">
              {complimentaryItems.map((item, idx) => (
                <span key={`${item}-${idx}`}>🎁 {item}</span>
              ))}
            </div>
          </div>
        )}

        {customFields.length > 0 && (
          <div className="tc-extra-summary-block">
            <span className="tc-extra-summary-label">Additional Fields</span>
            {customFields.map((field) => (
              <div
                className="tc-extra-summary-row tc-extra-custom-row"
                key={field.id}
              >
                <span>{field.label || "Custom Field"}</span>
                <strong>{field.value || "—"}</strong>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderCustomValueInput = (field, index) => {
    const valueError = editFieldErrors[`custom_${index}_value`];
    const commonClassName = `tc-form-input ${
      valueError ? "tc-input-error" : ""
    }`;

    if (field.type === "textarea") {
      return (
        <textarea
          className={`tc-form-textarea ${valueError ? "tc-input-error" : ""}`}
          rows={3}
          value={field.value}
          placeholder="Enter field value"
          onChange={(e) => updateCustomField(index, { value: e.target.value })}
        />
      );
    }

    if (field.type === "dropdown") {
      return (
        <select
          className={commonClassName}
          value={field.value}
          onChange={(e) => updateCustomField(index, { value: e.target.value })}
        >
          <option value="">Select an option</option>
          {field.options
            .map(trimValue)
            .filter(Boolean)
            .map((opt, oi) => (
              <option key={`${opt}-${oi}`} value={opt}>
                {opt}
              </option>
            ))}
        </select>
      );
    }

    if (field.type === "radio") {
      return (
        <div
          className={`tc-custom-radio-preview ${
            valueError ? "tc-radio-group-error" : ""
          }`}
        >
          {field.options
            .map(trimValue)
            .filter(Boolean)
            .map((opt, oi) => (
              <label className="tc-inline-radio" key={`${opt}-${oi}`}>
                <input
                  type="radio"
                  name={`custom-radio-${field.id}`}
                  value={opt}
                  checked={field.value === opt}
                  onChange={(e) =>
                    updateCustomField(index, { value: e.target.value })
                  }
                />
                <span>{opt}</span>
              </label>
            ))}
          {field.value && (
            <button
              type="button"
              className="tc-text-button"
              onClick={() => updateCustomField(index, { value: "" })}
            >
              Clear selection
            </button>
          )}
        </div>
      );
    }

    return (
      <input
        type={["number", "date"].includes(field.type) ? field.type : "text"}
        className={commonClassName}
        value={field.value}
        placeholder="Enter field value"
        onChange={(e) => updateCustomField(index, { value: e.target.value })}
      />
    );
  };

  const Th = ({ k, label }) => (
    <th
      className={`tc-th ${sortKey === k ? "tc-th-active" : ""}`}
      onClick={() => handleSort(k)}
    >
      {label}
      <span className="tc-sort-icon">
        {sortKey === k ? (sortDir === "asc" ? "↑" : "↓") : "⇅"}
      </span>
    </th>
  );

  if (loading) {
    return (
      <div className="tc-loading-container">
        <div className="tc-spinner" />
        <p>Loading bookings...</p>
      </div>
    );
  }

  return (
    <div className="tc-page">
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={closeToast} />
      )}

      <div className="tc-header">
        <div className="tc-header-left">
          <h1 className="tc-title">
            <span className="tc-title-icon">📋</span>
            Transfer Contact
          </h1>
          <p className="tc-subtitle">
            {activeTab === "pending"
              ? "Pending advance bookings ready for handover to staff"
              : "Complete history of all transferred bookings"}
          </p>
        </div>

        <div className="tc-header-right">
          <div className="tc-stats-bar">
            <div className="tc-stat-chip tc-stat-pending">
              <span>⏳</span>
              <strong>{orders.length}</strong>
              Pending
            </div>
            <div className="tc-stat-chip tc-stat-done">
              <span>✅</span>
              <strong>{transferred.length}</strong>
              Transferred
            </div>
            {demoMode && (
              <div className="tc-stat-chip tc-stat-demo">
                <span>🧪</span>
                Demo mode
              </div>
            )}
          </div>

          <div className="tc-filters-row">
            <div className="tc-date-filter-wrap">
              <span className="tc-filter-label">Event:</span>
              <input
                type="date"
                className="tc-date-input"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                title="From date"
              />
              <span className="tc-filter-sep">to</span>
              <input
                type="date"
                className="tc-date-input"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                title="To date"
              />
              {(fromDate || toDate) && (
                <button
                  type="button"
                  className="tc-clear-dates-btn"
                  onClick={() => {
                    setFromDate("");
                    setToDate("");
                  }}
                  title="Clear dates"
                >
                  ✕
                </button>
              )}
            </div>

            <div className="tc-search-wrap">
              <span className="tc-search-icon">🔍</span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, theater, phone..."
                className="tc-search-input"
              />
              {search && (
                <button
                  type="button"
                  className="tc-search-clear"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="tc-tabs">
        <button
          type="button"
          onClick={() => setActiveTab("pending")}
          className={`tc-tab ${activeTab === "pending" ? "tc-tab-active" : ""}`}
        >
          <span>⏳</span>
          Pending Transfer
          <span className="tc-tab-badge">{orders.length}</span>
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("transferred")}
          className={`tc-tab ${activeTab === "transferred" ? "tc-tab-active" : ""}`}
        >
          <span>📦</span>
          Transferred List
          <span className="tc-tab-badge">{transferred.length}</span>
        </button>
      </div>

      {/* ─── PENDING TAB ─── */}
      {activeTab === "pending" && (
        <div className="tc-table-outer">
          {filteredPending.length === 0 ? (
            <div className="tc-empty">
              <div className="tc-empty-icon">📭</div>
              <h3>No pending orders found</h3>
              <p>Try adjusting your search or date filters.</p>
            </div>
          ) : (
            <div className="tc-table-scroll">
              <table className="tc-table">
                <thead>
                  <tr>
                    <th className="tc-th tc-th-num">#</th>
                    <Th k="createdAt" label="Booked On" />
                    <Th k="name" label="Customer" />
                    <Th k="theater" label="Theater" />
                    <Th k="date" label="Event Date" />
                    <th className="tc-th">Slot</th>
                    <Th k="occasion" label="Occasion" />
                    <th className="tc-th">Persons</th>
                    <Th k="total" label="Amount" />
                    <th className="tc-th">Add-ons</th>
                    <th className="tc-th tc-th-action">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPending.map((o, i) => {
                    const addons = buildAddonRows(o);
                    const pricing = computeExtraPersonPricing(
                      hydrateEditForm(o, "pending")
                    );
                    const summary = computeBookingSummary(
                      o,
                      hydrateEditForm(o, "pending")
                    );

                    return (
                      <tr key={o._id || i} className="tc-row">
                        <td>
                          <span className="tc-row-num">{i + 1}</span>
                        </td>
                        <td>
                          <span className="tc-date-ts">
                            {formatTS(o.createdAt || o.timestamp)}
                          </span>
                        </td>
                        <td>
                          <div className="tc-customer">
                            <div className="tc-avatar">
                              {(o.name || o.customerName || "?")[0].toUpperCase()}
                            </div>
                            <div className="tc-customer-info">
                              <b>{o.name || o.customerName || "—"}</b>
                              <span className="tc-phone">
                                {o.whatsapp ||
                                  o.primaryContactNumber ||
                                  "—"}
                              </span>
                              {o.celebrationName && (
                                <small className="tc-celebration">
                                  🎉 {o.celebrationName}
                                </small>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="tc-theater-pill">
                            {o.theater || "—"}
                          </span>
                        </td>
                        <td>
                          <b className="tc-event-date">{o.date || "—"}</b>
                        </td>
                        <td>
                          <span className="tc-slot">{o.timeSlot || "—"}</span>
                        </td>
                        <td>
                          <span className="tc-occasion-pill">
                            {o.occasion || "—"}
                          </span>
                        </td>
                        <td>
                          <div className="tc-guests">
                            <span>👤 {n(o.adults || o.persons)}</span>
                            {n(o.kids) > 0 && <span>🧒 {n(o.kids)}</span>}
                            {pricing.extraPersons > 0 && (
                              <span className="tc-guests-extra">
                                ➕ {pricing.extraPersons} extra
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="tc-amount-cell">
                            <b>{money(summary.finalTotal)}</b>
                            <span className="tc-advance">
                              {money(o.advancePaid)} advance
                            </span>
                            {pricing.extraPersonAmount > 0 && (
                              <span className="tc-advance tc-advance-extra">
                                +{money(pricing.extraPersonAmount)} extra
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          {addons.length === 0 ? (
                            <span className="tc-no-addon">—</span>
                          ) : (
                            <div className="tc-addon-chips">
                              {addons.slice(0, 2).map((a, ai) => (
                                <span
                                  key={`${a.name}-${ai}`}
                                  className={`tc-addon-chip ${
                                    safeQty(a) > 1 ? "tc-addon-chip-has-qty" : ""
                                  }`}
                                  title={
                                    `${a.name}${qtyNosLabel(a)} | Unit ${money(
                                      addonDisplayPrice(a)
                                    )} | Total ${money(addonLineTotal(a))}`
                                  }
                                >
                                  <span>{a.emoji}</span>
                                  <span className="tc-addon-chip-name">
                                    {a.name}
                                  </span>
                                  {safeQty(a) > 1 && (
                                    <b className="tc-addon-chip-count">
                                      {qtyChipLabel(a)}
                                    </b>
                                  )}
                                </span>
                              ))}
                              {addons.length > 2 && (
                                <b className="tc-addon-more">
                                  +{addons.length - 2}
                                </b>
                              )}
                            </div>
                          )}
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="tc-actions-group">
                            <button
                              type="button"
                              className="tc-btn-edit"
                              onClick={() => openEditModal(o, "pending")}
                              title="Edit booking and staff handover details"
                            >
                              ✏️ Edit
                            </button>
                            <button
                              type="button"
                              className="tc-btn-transfer"
                              onClick={() => openTransferModal(o)}
                              title="Transfer to Staff Management"
                            >
                              👉 Transfer
                            </button>
                            <button
                              type="button"
                              className="tc-btn-delete"
                              onClick={() => openDeletePendingModal(o)}
                              title="Permanently delete this order"
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ─── TRANSFERRED TAB ─── */}
      {activeTab === "transferred" && (
        <div className="tc-table-outer">
          {filteredTransferred.length === 0 ? (
            <div className="tc-empty">
              <div className="tc-empty-icon">📦</div>
              <h3>No transferred bookings yet</h3>
              <p>Try adjusting your search or date filters.</p>
            </div>
          ) : (
            <div className="tc-table-scroll">
              <table className="tc-table">
                <thead>
                  <tr>
                    <th className="tc-th tc-th-num">#</th>
                    <th className="tc-th">Transferred On</th>
                    <th className="tc-th">Customer</th>
                    <th className="tc-th">WhatsApp</th>
                    <th className="tc-th">Theater</th>
                    <th className="tc-th">Event Date</th>
                    <th className="tc-th">Slot</th>
                    <th className="tc-th">Occasion</th>
                    <th className="tc-th">Amount</th>
                    <th className="tc-th">Transferred By</th>
                    <th className="tc-th">Status</th>
                    <th className="tc-th tc-th-action">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransferred.map((t, i) => (
                    <tr key={t._id || i} className="tc-row">
                      <td>
                        <span className="tc-row-num">{i + 1}</span>
                      </td>
                      <td>
                        <span className="tc-date-ts">
                          {formatTS(t.transferredAt)}
                        </span>
                      </td>
                      <td>
                        <div className="tc-customer">
                          <div className="tc-avatar tc-avatar-transferred">
                            {(t.customerName || t.name || "?")[0].toUpperCase()}
                          </div>
                          <div className="tc-customer-info">
                            <b>{t.customerName || t.name || "—"}</b>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="tc-phone">
                          {t.whatsapp || t.primaryContactNumber || "—"}
                        </span>
                      </td>
                      <td>
                        <span className="tc-theater-pill">
                          {t.theater || "—"}
                        </span>
                      </td>
                      <td>
                        <b className="tc-event-date">
                          {t.eventDate || t.date || "—"}
                        </b>
                      </td>
                      <td>
                        <span className="tc-slot">{t.timeSlot || "—"}</span>
                      </td>
                      <td>
                        <span className="tc-occasion-pill">
                          {t.occasion || "—"}
                        </span>
                      </td>
                      <td>
                        <b>{money(t.finalAmount || t.amount)}</b>
                      </td>
                      <td>
                        <span className="tc-transferred-by">
                          {t.transferredBy || "Admin"}
                        </span>
                      </td>
                      <td>
                        <span className="tc-status-badge tc-status-done">
                          ✅ In Staff Mgmt
                        </span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="tc-actions-group">
                          <button
                            type="button"
                            className="tc-btn-edit"
                            onClick={() => openEditModal(t, "transferred")}
                            title="Edit transferred booking details"
                          >
                            ✏️ Edit
                          </button>
                          <button
                            type="button"
                            className="tc-btn-delete"
                            onClick={() => openDeleteTransferModal(t)}
                            title="Permanently delete this transfer record"
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ═════════════════════════════════════════════════
          ADVANCED EDIT MODAL  (matches the reference design)
          ═════════════════════════════════════════════════ */}
      {editModalOpen && editTarget && (
        <div
          className="tc-modal-overlay tc-edit-overlay"
          onMouseDown={() => closeEditModal()}
        >
          <div
            className="tc-edit-modal-card"
            role="dialog"
            aria-modal="true"
            aria-label="Edit booking details"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="tc-edit-modal-header">
              <div className="tc-edit-title-row">
                <span className="tc-edit-title-icon">✏️</span>
                <div>
                  <h2>Edit Booking Details</h2>
                  <p>
                    Update customer, cake, decoration and staff handover
                    information.
                  </p>
                </div>
              </div>

              <div className="tc-edit-header-actions">
                <span
                  className={`tc-edit-source-badge ${
                    editSource === "pending"
                      ? "tc-edit-source-pending"
                      : "tc-edit-source-transferred"
                  }`}
                >
                  {editSource === "pending" ? "⏳ Pending" : "✅ Transferred"}
                </span>
                <button
                  type="button"
                  className="tc-modal-close-btn"
                  onClick={() => closeEditModal()}
                  disabled={isSavingEdit}
                  aria-label="Close editor"
                >
                  ✕
                </button>
              </div>
            </div>

            <form className="tc-edit-form" onSubmit={handleSaveEdit} noValidate>
              <div className="tc-edit-modal-body">
                {/* ── Customer Information ── */}
                <section className="tc-form-section">
                  <div className="tc-form-section-header">
                    <div>
                      <h3>👤 Customer Information</h3>
                      <p>Primary customer and contact information.</p>
                    </div>
                  </div>
                  <div className="tc-form-grid">
                    <label className="tc-form-field">
                      <span className="tc-form-label">
                        Customer Name <b className="tc-required">*</b>
                      </span>
                      <input
                        type="text"
                        className={`tc-form-input ${
                          editFieldErrors.customerName ? "tc-input-error" : ""
                        }`}
                        value={editForm.customerName}
                        onChange={(e) =>
                          updateEditField("customerName", e.target.value)
                        }
                        placeholder="Enter customer name"
                        autoComplete="name"
                      />
                      <FieldError message={editFieldErrors.customerName} />
                    </label>

                    <label className="tc-form-field">
                      <span className="tc-form-label">
                        Primary Contact Number <b className="tc-required">*</b>
                      </span>
                      <input
                        type="tel"
                        className={`tc-form-input ${
                          editFieldErrors.primaryContactNumber
                            ? "tc-input-error"
                            : ""
                        }`}
                        value={editForm.primaryContactNumber}
                        onChange={(e) =>
                          updateEditField("primaryContactNumber", e.target.value)
                        }
                        placeholder="+91 98765 43210"
                        autoComplete="tel"
                      />
                      <FieldError
                        message={editFieldErrors.primaryContactNumber}
                      />
                    </label>

                    <label className="tc-form-field">
                      <span className="tc-form-label">
                        Alternate Contact Number
                      </span>
                      <input
                        type="tel"
                        className={`tc-form-input ${
                          editFieldErrors.alternateContactNumber
                            ? "tc-input-error"
                            : ""
                        }`}
                        value={editForm.alternateContactNumber}
                        onChange={(e) =>
                          updateEditField(
                            "alternateContactNumber",
                            e.target.value
                          )
                        }
                        placeholder="Optional alternate number"
                        autoComplete="tel"
                      />
                      <FieldError
                        message={editFieldErrors.alternateContactNumber}
                      />
                    </label>

                    <label className="tc-form-field tc-field-full">
                      <span className="tc-form-label">Food Details</span>
                      <textarea
                        className="tc-form-textarea"
                        rows={3}
                        value={editForm.foodDetails}
                        onChange={(e) =>
                          updateEditField("foodDetails", e.target.value)
                        }
                        placeholder="Food menu, dietary requirements, serving notes..."
                      />
                    </label>
                  </div>
                </section>

                {/* ── Cake & Celebration ── */}
                <section className="tc-form-section">
                  <div className="tc-form-section-header">
                    <div>
                      <h3>🎂 Cake & Celebration</h3>
                      <p>
                        Configure cake, candle, sash and crown requirements.
                      </p>
                    </div>
                  </div>
                  <div className="tc-form-grid">
                    <label className="tc-form-field">
                      <span className="tc-form-label">Cake Message</span>
                      <input
                        type="text"
                        className="tc-form-input"
                        value={editForm.cakeMessage}
                        onChange={(e) =>
                          updateEditField("cakeMessage", e.target.value)
                        }
                        placeholder="Text to write on the cake"
                      />
                    </label>

                    <label className="tc-form-field">
                      <span className="tc-form-label">Cake Flavor</span>
                      <input
                        type="text"
                        list="tc-cake-flavors"
                        className="tc-form-input"
                        value={editForm.cakeFlavor}
                        onChange={(e) =>
                          updateEditField("cakeFlavor", e.target.value)
                        }
                        placeholder="Select or enter flavor"
                      />
                      <datalist id="tc-cake-flavors">
                        <option value="Chocolate" />
                        <option value="Vanilla" />
                        <option value="Butterscotch" />
                        <option value="Black Forest" />
                        <option value="Red Velvet" />
                        <option value="Pineapple" />
                        <option value="Blueberry" />
                        <option value="Strawberry" />
                      </datalist>
                    </label>

                    <label className="tc-form-field">
                      <span className="tc-form-label">Cake Weight</span>
                      <div className="tc-input-suffix-wrap">
                        <input
                          type="number"
                          min="0.25"
                          step="0.25"
                          className={`tc-form-input ${
                            editFieldErrors.cakeWeight ? "tc-input-error" : ""
                          }`}
                          value={editForm.cakeWeight}
                          onChange={(e) =>
                            updateEditField("cakeWeight", e.target.value)
                          }
                          placeholder="1"
                        />
                        <span>kg</span>
                      </div>
                      <FieldError message={editFieldErrors.cakeWeight} />
                    </label>

                    <label className="tc-form-field">
                      <span className="tc-form-label">Candle Number</span>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        className={`tc-form-input ${
                          editFieldErrors.candleNumber ? "tc-input-error" : ""
                        }`}
                        value={editForm.candleNumber}
                        onChange={(e) =>
                          updateEditField("candleNumber", e.target.value)
                        }
                        placeholder="Example: 25"
                      />
                      <FieldError message={editFieldErrors.candleNumber} />
                    </label>

                    

                    
                  </div>
                </section>

                {/* ── Decoration & Management ── */}
                <section className="tc-form-section">
                  <div className="tc-form-section-header">
                    <div>
                      <h3>🎨 Management</h3>
                      <p>
                        Manager-only discount, remarks and operational
                        instructions.
                      </p>
                    </div>
                  </div>
                  <div className="tc-form-grid">
                    

                    <label className="tc-form-field">
                      <span className="tc-form-label">Manager Discount</span>
                      <div className="tc-input-prefix-wrap">
                        <span>₹</span>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          className={`tc-form-input ${
                            editFieldErrors.managerDiscount
                              ? "tc-input-error"
                              : ""
                          }`}
                          value={editForm.managerDiscount}
                          onChange={(e) =>
                            updateEditField("managerDiscount", e.target.value)
                          }
                          placeholder="0"
                        />
                      </div>
                      <FieldError message={editFieldErrors.managerDiscount} />
                    </label>

                    <label className="tc-form-field tc-field-full">
                      <span className="tc-form-label">Manager Remarks</span>
                      <textarea
                        className="tc-form-textarea"
                        rows={3}
                        value={editForm.managerRemarks}
                        onChange={(e) =>
                          updateEditField("managerRemarks", e.target.value)
                        }
                        placeholder="Internal remarks visible to management..."
                      />
                    </label>

                    <label className="tc-form-field tc-field-full">
                      <span className="tc-form-label">Special Instructions</span>
                      <textarea
                        className="tc-form-textarea"
                        rows={3}
                        value={editForm.specialInstructions}
                        onChange={(e) =>
                          updateEditField("specialInstructions", e.target.value)
                        }
                        placeholder="Important instructions for staff..."
                      />
                    </label>
                  </div>
                </section>

                {/* ═══════════ EXTRA PERSON DETAILS ═══════════ */}
                <section className="tc-extra-person-card">
                  <div className="tc-extra-person-header">
                    <h3>👥 Extra Person Details</h3>
                    <p>Manage extra persons and per person charges</p>
                  </div>

                  <div className="tc-extra-person-grid">
                    <label className="tc-form-field">
                      <span className="tc-form-label">
                        Total Number of Persons
                      </span>
                      <div className="tc-input-prefix-wrap tc-input-icon-wrap">
                        <span className="tc-input-icon">👤</span>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          className={`tc-form-input ${
                            editFieldErrors.totalPersons ? "tc-input-error" : ""
                          }`}
                          value={editForm.totalPersons}
                          onChange={(e) =>
                            updateEditField("totalPersons", e.target.value)
                          }
                          placeholder="0"
                        />
                      </div>
                      <FieldError message={editFieldErrors.totalPersons} />
                    </label>

                    <label className="tc-form-field">
                      <span className="tc-form-label">
                        Package Included Persons
                      </span>
                      <div className="tc-input-prefix-wrap tc-input-icon-wrap">
                        <span className="tc-input-icon">🎁</span>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          className={`tc-form-input ${
                            editFieldErrors.packageIncludedPersons
                              ? "tc-input-error"
                              : ""
                          }`}
                          value={editForm.packageIncludedPersons}
                          onChange={(e) =>
                            updateEditField(
                              "packageIncludedPersons",
                              e.target.value
                            )
                          }
                          placeholder="0"
                        />
                      </div>
                      <FieldError
                        message={editFieldErrors.packageIncludedPersons}
                      />
                    </label>

                    <label className="tc-form-field">
                      <span className="tc-form-label">
                        Extra Persons <small>(Auto)</small>
                      </span>
                      <div className="tc-input-prefix-wrap tc-input-icon-wrap tc-auto-wrap">
                        <span className="tc-input-icon">👥</span>
                        <input
                          type="text"
                          className="tc-form-input tc-auto-input"
                          value={livePricing.extraPersons}
                          readOnly
                          tabIndex={-1}
                        />
                      </div>
                      <span className="tc-helper-note">
                        {livePricing.totalPersons} −{" "}
                        {livePricing.packageIncludedPersons} ={" "}
                        {livePricing.extraPersons}
                      </span>
                    </label>

                    <label className="tc-form-field">
                      <span className="tc-form-label">
                        Standard Cost Per Extra Person
                      </span>
                      <div className="tc-input-prefix-wrap">
                        <span>₹</span>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          className="tc-form-input"
                          value={editForm.standardCostPerPerson}
                          onChange={(e) =>
                            updateEditField(
                              "standardCostPerPerson",
                              e.target.value
                            )
                          }
                          placeholder="0"
                        />
                      </div>
                    </label>

                    <label className="tc-form-field">
                      <span className="tc-form-label">
                        Discount / Complimentary Per Person
                      </span>
                      <div className="tc-input-prefix-wrap">
                        <span>🏷️</span>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          className={`tc-form-input ${
                            editFieldErrors.discountPerPerson
                              ? "tc-input-error"
                              : ""
                          }`}
                          value={editForm.discountPerPerson}
                          onChange={(e) =>
                            updateEditField("discountPerPerson", e.target.value)
                          }
                          placeholder="0"
                        />
                      </div>
                      <FieldError message={editFieldErrors.discountPerPerson} />
                    </label>

                    <label className="tc-form-field">
                      <span className="tc-form-label">
                        Final Cost Per Extra Person <small>(Auto)</small>
                      </span>
                      <div className="tc-input-prefix-wrap tc-auto-wrap">
                        <span>₹</span>
                        <input
                          type="text"
                          className="tc-form-input tc-auto-input"
                          value={livePricing.finalCostPerPerson}
                          readOnly
                          tabIndex={-1}
                        />
                      </div>
                      <span className="tc-helper-note">
                        {livePricing.standardCostPerPerson} −{" "}
                        {livePricing.discountPerPerson} ={" "}
                        {livePricing.finalCostPerPerson}
                      </span>
                    </label>
                  </div>

                  {/* ★ NEW — complimentary persons are never charged */}
                  <div className="tc-comp-person-strip">
                    <div className="tc-comp-person-heading">
                      <span className="tc-comp-person-icon">🤝</span>
                      <div>
                        <b>Complimentary Persons</b>
                        <small>
                          Price for these persons is NOT calculated in the
                          booking total
                        </small>
                      </div>
                    </div>

                    <div className="tc-comp-person-fields">
                      <label className="tc-form-field">
                        <span className="tc-form-label">
                          Complimentary Persons <small>(No Charge)</small>
                        </span>
                        <div className="tc-input-prefix-wrap tc-input-icon-wrap">
                          <span className="tc-input-icon">🤝</span>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            max={livePricing.extraPersons}
                            className={`tc-form-input ${
                              editFieldErrors.complimentaryPersons
                                ? "tc-input-error"
                                : ""
                            }`}
                            value={editForm.complimentaryPersons}
                            onChange={(e) =>
                              updateEditField(
                                "complimentaryPersons",
                                e.target.value
                              )
                            }
                            placeholder="0"
                          />
                        </div>
                        <FieldError
                          message={editFieldErrors.complimentaryPersons}
                        />
                      </label>

                      <label className="tc-form-field">
                        <span className="tc-form-label">
                          Chargeable Extra Persons <small>(Auto)</small>
                        </span>
                        <div className="tc-input-prefix-wrap tc-input-icon-wrap tc-auto-wrap">
                          <span className="tc-input-icon">🧾</span>
                          <input
                            type="text"
                            className="tc-form-input tc-auto-input"
                            value={livePricing.chargeablePersons}
                            readOnly
                            tabIndex={-1}
                          />
                        </div>
                        <span className="tc-helper-note">
                          {livePricing.extraPersons} −{" "}
                          {livePricing.complimentaryPersons} ={" "}
                          {livePricing.chargeablePersons} charged
                        </span>
                      </label>

                      <label className="tc-form-field">
                        <span className="tc-form-label">
                          Complimentary Value{" "}
                          <small>(Informational)</small>
                        </span>
                        <div className="tc-input-prefix-wrap tc-auto-wrap">
                          <span>₹</span>
                          <input
                            type="text"
                            className="tc-form-input tc-auto-input"
                            value={
                              livePricing.complimentaryPersons *
                              livePricing.finalCostPerPerson
                            }
                            readOnly
                            tabIndex={-1}
                          />
                        </div>
                        <span className="tc-helper-note">
                          {livePricing.complimentaryPersons} ×{" "}
                          {livePricing.finalCostPerPerson} — not added to total
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="tc-extra-total-banner">
                    <div className="tc-extra-total-left">
                      <span className="tc-extra-total-icon">🧮</span>
                      <div>
                        <span className="tc-extra-total-label">
                          Total Extra Person Amount <b>(Auto)</b>
                        </span>
                        <strong className="tc-extra-total-value">
                          {money(livePricing.extraPersonAmount)}
                        </strong>
                        <span className="tc-extra-total-note">
                          {extraBreakdown}
                          {livePricing.complimentaryPersons > 0
                            ? ` · ${livePricing.complimentaryPersons} complimentary person(s) excluded from billing`
                            : ""}
                        </span>
                      </div>
                    </div>
                    <div className="tc-extra-total-right">
                      <span className="tc-extra-total-check">✅</span>
                      <span>
                        This amount will be added to the final booking total
                      </span>
                    </div>
                  </div>
                </section>

                {/* ── Complimentary Items + Booking Summary ── */}
                <div className="tc-two-column-row">
                  <section className="tc-form-section tc-comp-items-section">
                    <div className="tc-form-section-header">
                      <div>
                        <h3>🎁 Complimentary Items</h3>
                        <p>
                          Add complimentary products or services included with
                          the booking.
                        </p>
                      </div>
                    </div>

                    <div className="tc-tag-input-row">
                      <input
                        type="text"
                        className="tc-form-input"
                        value={complimentaryDraft}
                        onChange={(e) => setComplimentaryDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === ",") {
                            e.preventDefault();
                            addComplimentaryItems();
                          }
                        }}
                        placeholder="Type an item and press Enter"
                      />
                      <button
                        type="button"
                        className="tc-btn-add-item"
                        onClick={() => addComplimentaryItems()}
                      >
                        ＋ Add Item
                      </button>
                    </div>

                    {editForm.complimentaryItems.length > 0 ? (
                      <div className="tc-complimentary-list">
                        {editForm.complimentaryItems.map((item, idx) => (
                          <span
                            className="tc-complimentary-chip"
                            key={`${item}-${idx}`}
                          >
                            {item}
                            <button
                              type="button"
                              onClick={() => removeComplimentaryItem(idx)}
                              aria-label={`Remove ${item}`}
                            >
                              ✕
                            </button>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="tc-muted-hint">
                        No complimentary items added.
                      </p>
                    )}
                  </section>

                  <aside className="tc-summary-card">
                    <div className="tc-summary-heading">
                      <span>🧾</span>
                      <b>Booking Summary</b>
                    </div>

                    <div className="tc-summary-row">
                      <span>Package Amount</span>
                      <b>{money(liveSummary.packageAmount)}</b>
                    </div>
                    <div className="tc-summary-row">
                      <span>Extra Person Amount</span>
                      <b>{money(liveSummary.extraPersonAmount)}</b>
                    </div>
                    <div className="tc-summary-row">
                      <span>Add-ons / Other Charges</span>
                      <b>{money(liveSummary.addonAmount)}</b>
                    </div>
                    <div className="tc-summary-row">
                      <span>Discounts</span>
                      <b className="tc-summary-negative">
                        - {money(liveSummary.discounts)}
                      </b>
                    </div>

                    <div className="tc-summary-divider" />

                    <div className="tc-summary-final">
                      <span>Final Booking Total</span>
                      <strong>{money(liveSummary.finalTotal)}</strong>
                    </div>
                  </aside>
                </div>

                {/* ── Custom Fields ── */}
                <section className="tc-form-section tc-custom-fields-section">
                  <div className="tc-form-section-header tc-custom-section-header">
                    <div>
                      <h3>⚙️ Additional Custom Fields</h3>
                      <p>
                        Add normal inputs, dropdowns, radio buttons, dates,
                        numbers or long text fields.
                      </p>
                    </div>
                    <button
                      type="button"
                      className="tc-btn-add-field"
                      onClick={addCustomField}
                    >
                      ＋ Add New Field
                    </button>
                  </div>

                  {editForm.customFields.length === 0 ? (
                    <div className="tc-custom-empty">
                      <div>🧩</div>
                      <h4>No custom fields added</h4>
                      <p>
                        Click "Add New Field" to collect additional booking
                        information.
                      </p>
                      <button
                        type="button"
                        className="tc-btn-add-field"
                        onClick={addCustomField}
                      >
                        ＋ Add First Field
                      </button>
                    </div>
                  ) : (
                    <div className="tc-custom-fields-list">
                      {editForm.customFields.map((field, fi) => {
                        const labelError =
                          editFieldErrors[`custom_${fi}_label`];
                        const optionError =
                          editFieldErrors[`custom_${fi}_options`];
                        const valueError =
                          editFieldErrors[`custom_${fi}_value`];

                        return (
                          <div className="tc-custom-field-card" key={field.id}>
                            <div className="tc-custom-field-header">
                              <div className="tc-custom-field-number">
                                Field {fi + 1}
                              </div>
                              <button
                                type="button"
                                className="tc-custom-remove-btn"
                                onClick={() => removeCustomField(fi)}
                              >
                                🗑️ Remove
                              </button>
                            </div>

                            <div className="tc-custom-config-grid">
                              <label className="tc-form-field">
                                <span className="tc-form-label">
                                  Field Label <b className="tc-required">*</b>
                                </span>
                                <input
                                  type="text"
                                  className={`tc-form-input ${
                                    labelError ? "tc-input-error" : ""
                                  }`}
                                  value={field.label}
                                  onChange={(e) =>
                                    updateCustomField(fi, {
                                      label: e.target.value,
                                    })
                                  }
                                  placeholder="Example: Balloon Color"
                                />
                                <FieldError message={labelError} />
                              </label>

                              <label className="tc-form-field">
                                <span className="tc-form-label">
                                  Field Type
                                </span>
                                <select
                                  className="tc-form-input"
                                  value={field.type}
                                  onChange={(e) =>
                                    handleCustomFieldTypeChange(
                                      fi,
                                      e.target.value
                                    )
                                  }
                                >
                                  {CUSTOM_FIELD_TYPES.map((ft) => (
                                    <option key={ft.value} value={ft.value}>
                                      {ft.label}
                                    </option>
                                  ))}
                                </select>
                              </label>

                              <label className="tc-required-toggle">
                                <input
                                  type="checkbox"
                                  checked={field.required}
                                  onChange={(e) =>
                                    updateCustomField(fi, {
                                      required: e.target.checked,
                                    })
                                  }
                                />
                                <span className="tc-toggle-ui" />
                                <span>Required field</span>
                              </label>
                            </div>

                            {["dropdown", "radio"].includes(field.type) && (
                              <div className="tc-custom-options">
                                <div className="tc-custom-options-heading">
                                  <div>
                                    <b>Field Options</b>
                                    <small>Add at least two unique options.</small>
                                  </div>
                                  <button
                                    type="button"
                                    className="tc-btn-small-add"
                                    onClick={() => addCustomOption(fi)}
                                  >
                                    ＋ Add Option
                                  </button>
                                </div>
                                <div className="tc-custom-option-list">
                                  {field.options.map((opt, oi) => (
                                    <div
                                      className="tc-custom-option-row"
                                      key={`${field.id}-opt-${oi}`}
                                    >
                                      <span>{oi + 1}</span>
                                      <input
                                        type="text"
                                        className="tc-form-input"
                                        value={opt}
                                        onChange={(e) =>
                                          updateCustomOption(
                                            fi,
                                            oi,
                                            e.target.value
                                          )
                                        }
                                        placeholder={`Option ${oi + 1}`}
                                      />
                                      <button
                                        type="button"
                                        onClick={() =>
                                          removeCustomOption(fi, oi)
                                        }
                                        title="Remove option"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  ))}
                                </div>
                                <FieldError message={optionError} />
                              </div>
                            )}

                            <div className="tc-custom-preview">
                              <div className="tc-custom-preview-title">
                                <span>Preview & Value</span>
                                {field.required && <small>Required</small>}
                              </div>
                              <label className="tc-form-field">
                                <span className="tc-form-label">
                                  {field.label || "Custom field"}
                                </span>
                                {renderCustomValueInput(field, fi)}
                                <FieldError message={valueError} />
                              </label>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>

                {editRequestError && (
                  <div className="tc-modal-error tc-edit-message">
                    ⚠️ {editRequestError}
                  </div>
                )}
                {editSuccess && (
                  <div className="tc-modal-success tc-edit-message">
                    ✅ {editSuccess}
                  </div>
                )}

                {/* ── Sticky action bar ── */}
                <div className="tc-edit-modal-footer">
                  <div className="tc-edit-footer-info">
                    <span>ℹ️</span>
                    Changes are saved directly to this booking.
                  </div>
                  <div className="tc-edit-footer-actions">
                    <button
                      type="button"
                      className="tc-btn-secondary"
                      onClick={() => closeEditModal()}
                      disabled={isSavingEdit}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="tc-btn-save"
                      disabled={isSavingEdit}
                    >
                      {isSavingEdit ? (
                        <>
                          <span className="tc-button-spinner" />
                          Saving Changes...
                        </>
                      ) : (
                        <>💾 Save Changes</>
                      )}
                    </button>
                  </div>
                </div>

                
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═════════════════════════════════════════════════
          CONFIRMATION MODALS
          ═════════════════════════════════════════════════ */}
      <ConfirmModal
        open={transferModalOpen && !!selectedOrder}
        title="📤 Confirm Transfer"
        description="You are about to move this booking to Staff Management."
        details={
          selectedOrder && (
            <>
              <div className="tc-detail-row">
                <span>Customer</span>
                <strong>{selectedOrder.name}</strong>
              </div>
              <div className="tc-detail-row">
                <span>Theater</span>
                <strong>{selectedOrder.theater}</strong>
              </div>
              <div className="tc-detail-row">
                <span>Event Date</span>
                <strong>{selectedOrder.date}</strong>
              </div>
              <div className="tc-detail-row">
                <span>Phone</span>
                <strong>
                  {selectedOrder.whatsapp ||
                    selectedOrder.primaryContactNumber}
                </strong>
              </div>
              <div className="tc-detail-row">
                <span>Amount</span>
                <strong>
                  {money(
                    computeBookingSummary(
                      selectedOrder,
                      hydrateEditForm(selectedOrder, "pending")
                    ).finalTotal
                  )}
                </strong>
              </div>
              {renderAdditionalDetails(selectedOrder)}
              {renderAddonDetails(selectedOrder)}
            </>
          )
        }
        confirmLabel="Yes, Transfer Now"
        confirmColor="#4f46e5"
        isProcessing={isTransferring}
        processingLabel="Transferring..."
        error={transferError}
        success={transferSuccess}
        onConfirm={handleTransferClick}
        onCancel={closeTransferModal}
      />

      <ConfirmModal
        open={deletePendingModalOpen && !!deletePendingTarget}
        title="🗑️ Delete Pending Order"
        description="This will permanently delete this order from the system. This action cannot be undone."
        warningText="All order data, associated bookings, and transfer records will be permanently removed."
        details={
          deletePendingTarget && (
            <>
              <div className="tc-detail-row">
                <span>Customer</span>
                <strong>{deletePendingTarget.name}</strong>
              </div>
              <div className="tc-detail-row">
                <span>Theater</span>
                <strong>{deletePendingTarget.theater}</strong>
              </div>
              <div className="tc-detail-row">
                <span>Event Date</span>
                <strong>{deletePendingTarget.date}</strong>
              </div>
              <div className="tc-detail-row">
                <span>Phone</span>
                <strong>
                  {deletePendingTarget.whatsapp ||
                    deletePendingTarget.primaryContactNumber}
                </strong>
              </div>
              <div className="tc-detail-row">
                <span>Amount</span>
                <strong>
                  {money(
                    computeBookingSummary(
                      deletePendingTarget,
                      hydrateEditForm(deletePendingTarget, "pending")
                    ).finalTotal
                  )}
                </strong>
              </div>
              <div className="tc-detail-row">
                <span>Booked On</span>
                <strong>{formatTS(deletePendingTarget.createdAt)}</strong>
              </div>
              {renderAdditionalDetails(deletePendingTarget)}
              {renderAddonDetails(deletePendingTarget)}
            </>
          )
        }
        confirmLabel="Yes, Delete Permanently"
        confirmColor="#dc2626"
        isProcessing={isDeletingPending}
        processingLabel="Deleting..."
        error={deletePendingError}
        success={deletePendingSuccess}
        onConfirm={handleDeletePendingConfirm}
        onCancel={closeDeletePendingModal}
      />

      <ConfirmModal
        open={deleteTransferModalOpen && !!deleteTransferTarget}
        title="🗑️ Delete Transfer Record"
        description="This will permanently delete this transfer record and its associated booking from the system."
        warningText="The transfer record, associated booking, and staff assignment will be permanently removed. This cannot be undone."
        details={
          deleteTransferTarget && (
            <>
              <div className="tc-detail-row">
                <span>Customer</span>
                <strong>{deleteTransferTarget.customerName}</strong>
              </div>
              <div className="tc-detail-row">
                <span>Theater</span>
                <strong>{deleteTransferTarget.theater}</strong>
              </div>
              <div className="tc-detail-row">
                <span>Event Date</span>
                <strong>{deleteTransferTarget.eventDate}</strong>
              </div>
              <div className="tc-detail-row">
                <span>Amount</span>
                <strong>
                  {money(
                    deleteTransferTarget.finalAmount ||
                      deleteTransferTarget.amount
                  )}
                </strong>
              </div>
              <div className="tc-detail-row">
                <span>Transferred On</span>
                <strong>{formatTS(deleteTransferTarget.transferredAt)}</strong>
              </div>
              {renderAdditionalDetails(deleteTransferTarget)}
            </>
          )
        }
        confirmLabel="Yes, Delete Permanently"
        confirmColor="#dc2626"
        isProcessing={isDeletingTransfer}
        processingLabel="Deleting..."
        error={deleteTransferError}
        success={deleteTransferSuccess}
        onConfirm={handleDeleteTransferConfirm}
        onCancel={closeDeleteTransferModal}
      />
    </div>
  );
};

export default TransferContact;
